import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/utils/apiClient";
import { AdminRole, AdminRoleInput, AdminRoleUpdate, PermissionsByResource } from "@/types/rbac";

interface RolesResponse {
  roles: AdminRole[];
}

interface RoleResponse {
  role: AdminRole;
}

interface PermissionsResponse {
  permissions: PermissionsByResource;
}

export function useRoles() {
  return useQuery({
    queryKey: ["admin-roles"],
    queryFn: async () => {
      const { roles } = await apiRequest<RolesResponse>("/api/admin/roles");
      return roles;
    },
  });
}

export function useRole(roleId: string | null) {
  return useQuery({
    queryKey: ["admin-role", roleId],
    queryFn: async () => {
      if (!roleId) return null;
      const { role } = await apiRequest<RoleResponse>(`/api/admin/roles/${roleId}`);
      return role;
    },
    enabled: !!roleId,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AdminRoleInput) => {
      const { role } = await apiRequest<RoleResponse>("/api/admin/roles", {
        method: "POST",
        body: JSON.stringify(input),
      });
      return role;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roleId, data }: { roleId: string; data: AdminRoleUpdate }) => {
      const { role } = await apiRequest<RoleResponse>(`/api/admin/roles/${roleId}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
      return role;
    },
    onSuccess: (role) => {
      queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
      queryClient.invalidateQueries({ queryKey: ["admin-role", role.roleId] });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleId: string) => {
      await apiRequest(`/api/admin/roles/${roleId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
    },
  });
}

export function useUpdateRolePermissions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roleId, permissions }: { roleId: string; permissions: string[] }) => {
      const { role } = await apiRequest<RoleResponse>(`/api/admin/roles/${roleId}/permissions`, {
        method: "PUT",
        body: JSON.stringify({ permissions }),
      });
      return role;
    },
    onSuccess: (role) => {
      queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
      queryClient.invalidateQueries({ queryKey: ["admin-role", role.roleId] });
    },
  });
}

export function useAvailablePermissions() {
  return useQuery({
    queryKey: ["admin-permissions"],
    queryFn: async () => {
      const { permissions } = await apiRequest<PermissionsResponse>("/api/admin/permissions");
      return permissions;
    },
  });
}
